import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import RatingMessageField from "../components/RatingMessageField";
import { ArrowLeft, Star, Activity, History, Smile, BookOpen, Target, Gauge, CheckCircle2, Clock, ChevronRight, Flame, Trophy } from "lucide-react";
import { toast } from "sonner";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';
import { useTheme, useThemeClasses } from "../hooks/useTheme";
import { useUser } from "../context/UserContext";
import { API } from "../utils/constants";
import { RatingSlider } from "../components/RatingSlider";
import { formatRating } from "../utils/ratings";
import repairAthleteSessionTimestamps from "../utils/sessionTimestampRepair";

// Horizontal Bar Chart Component for rated sessions
const RatingBarChart = ({ label, value, icon: Icon, color, bgColor, isDark }) => {
  const percentage = (value / 10) * 100;
  return (
    <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${color}`} />
          <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>{label}</span>
        </div>
        <span className={`text-lg font-bold ${color}`}>{formatRating(value)}</span>
      </div>
      <div className={`h-3 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-200'} overflow-hidden`}>
        <div 
          className={`h-full rounded-full transition-all duration-700 ease-out ${bgColor}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

export const LastTrainingPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const theme = useThemeClasses(isDark);
  const { user, isCoach } = useUser();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const messageRef = useRef(null);
  const [ratings, setRatings] = useState({ fun: 5, learning: 5, mygoal: 5, intensity: 5 });
  const [teamStats, setTeamStats] = useState(null);
  const [avgLast10, setAvgLast10] = useState({ fun: 0, learning: 0, mygoal: 0, intensity: 0 });
  const [minRatings, setMinRatings] = useState({ fun: 0, learning: 0, mygoal: 0, intensity: 0 });
  const [maxRatings, setMaxRatings] = useState({ fun: 0, learning: 0, mygoal: 0, intensity: 0 });
  const [unratedSessions, setUnratedSessions] = useState([]);
  const [justRated, setJustRated] = useState(false);
  const [showStreakBonus, setShowStreakBonus] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isCoach && user?.id) {
          const teamRes = await axios.get(`${API}/coach/team/last-training?coach_id=${user.id}`);
          setTeamStats(teamRes.data.team_stats);
          setAvgLast10(teamRes.data.average_last_10 || { fun: 0, learning: 0, mygoal: 0, intensity: 0 });
          // Set min/max ratings for bandwidth display
          if (teamRes.data.team_stats?.current_min) {
            setMinRatings(teamRes.data.team_stats.current_min);
          }
          if (teamRes.data.team_stats?.current_max) {
            setMaxRatings(teamRes.data.team_stats.current_max);
          }
          if (teamRes.data.last_session) {
            setSession({
              name: teamRes.data.last_session.name,
              type: teamRes.data.last_session.type,
              duration: teamRes.data.last_session.duration,
              completed_at: teamRes.data.last_session.completed_at,
              rating: teamRes.data.team_stats?.current_averages || null
            });
            if (teamRes.data.team_stats?.current_averages) {
              const avg = teamRes.data.team_stats.current_averages;
              setRatings({ fun: avg.fun || 5, learning: avg.learning || 5, mygoal: avg.mygoal || 5, intensity: avg.intensity || 5 });
            }
          }
        } else if (user?.id) {
          // Athlete: fetch only their own last session
          await repairAthleteSessionTimestamps(user.id);
          const res = await axios.get(`${API}/sessions/last?user_id=${user.id}`);
          setSession(res.data);
          if (res.data.rating) {
            setRatings({ fun: res.data.rating.fun || 5, learning: res.data.rating.learning || 5, mygoal: res.data.rating.mygoal || 5, intensity: res.data.rating.intensity || 5 });
          }
          // Also fetch last 10 average for the athlete and find unrated sessions
          try {
            const sessionsRes = await axios.get(`${API}/sessions?user_id=${user.id}`);
            const allSessions = sessionsRes.data;
            const ratedSessions = allSessions.filter(s => s.rating);
            // Find unrated sessions (excluding the current one)
            const unrated = allSessions.filter(s => !s.rating && s.id !== res.data?.id);
            setUnratedSessions(unrated.slice(0, 3)); // Show max 3 suggestions
            
            if (ratedSessions.length > 0) {
              const avgFun = ratedSessions.reduce((sum, s) => sum + (s.rating?.fun || 0), 0) / ratedSessions.length;
              const avgLearning = ratedSessions.reduce((sum, s) => sum + (s.rating?.learning || 0), 0) / ratedSessions.length;
              const avgMygoal = ratedSessions.reduce((sum, s) => sum + (s.rating?.mygoal || 0), 0) / ratedSessions.length;
              const avgIntensity = ratedSessions.reduce((sum, s) => sum + (s.rating?.intensity || 0), 0) / ratedSessions.length;
              setAvgLast10({ fun: avgFun, learning: avgLearning, mygoal: avgMygoal, intensity: avgIntensity });
            }
          } catch (e) {
            console.warn('Failed to compute avgLast10:', e);
          }
        }
      } catch (e) {
        console.error("Error fetching last session:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isCoach, user?.id]);

  const handleSaveRating = async () => {
    if (!session) return;
    if (messageRef.current && !messageRef.current.validate()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/sessions/${session.id}/rate`, ratings);
      await messageRef.current?.send(session.id);
      toast.success("Training rated successfully!");
      setJustRated(true);
      // Update session to show as rated
      setSession({ ...session, rating: ratings });
      // Refresh unrated sessions
      try {
        const sessionsRes = await axios.get(`${API}/sessions?user_id=${user.id}`);
        const unrated = sessionsRes.data.filter(s => !s.rating && s.id !== session.id);
        setUnratedSessions(unrated.slice(0, 3));
        // If all trainings are now rated, show streak bonus!
        if (unrated.length === 0) {
          setShowStreakBonus(true);
        }
      } catch (e) {
        console.warn('Failed to refresh unrated sessions:', e);
      }
    } catch (e) {
      if (e.message !== "MESSAGE_SEND_FAILED") toast.error("Failed to save rating");
    } finally {
      setSaving(false);
    }
  };

  const handleRateOlderSession = (olderSession) => {
    // Navigate to rate this specific session
    navigate(`/rate-session/${olderSession.id}`);
  };

  const ratingTopics = [
    { key: 'fun', label: 'Fun', question: { en: 'Did I enjoy today?', nl: 'Heb ik vandaag genoten?' }, tooltip: { en: 'Did this session feel enjoyable and motivating?', nl: 'Voelde deze sessie leuk en motiverend?' }, icon: Smile, color: 'text-yellow-500', bgColor: 'bg-gradient-to-r from-yellow-400 to-yellow-500' },
    { key: 'learning', label: 'Learning', question: { en: 'Did I grow today?', nl: 'Ben ik vandaag gegroeid?' }, tooltip: { en: 'Did I learn or improve a skill today?', nl: 'Heb ik vandaag een vaardigheid geleerd of verbeterd?' }, icon: BookOpen, color: 'text-blue-500', bgColor: 'bg-gradient-to-r from-blue-400 to-blue-500' },
    { key: 'mygoal', label: 'MyGoal', question: { en: 'Did I develop MyGoal?', nl: 'Heb ik mijn MyGoal ontwikkeld?' }, tooltip: { en: 'Did this session move me forward on MyGoal?', nl: 'Heeft deze sessie mij vooruit geholpen op mijn MyGoal?' }, icon: Target, color: 'text-purple-500', bgColor: 'bg-gradient-to-r from-purple-400 to-purple-500' },
    { key: 'intensity', label: 'Intensity', question: { en: 'Did I push myself?', nl: 'Heb ik mezelf uitgedaagd?' }, tooltip: { en: 'Did I train with enough effort and focus?', nl: 'Trainde ik met voldoende inzet en focus?' }, icon: Gauge, color: 'text-red-500', bgColor: 'bg-gradient-to-r from-red-400 to-red-500' }
  ];

  const radarData = [
    { subject: 'Fun', value: ratings.fun, average: avgLast10.fun, min: minRatings.fun, max: maxRatings.fun, fullMark: 10 },
    { subject: 'Learning', value: ratings.learning, average: avgLast10.learning, min: minRatings.learning, max: maxRatings.learning, fullMark: 10 },
    { subject: 'MyGoal', value: ratings.mygoal, average: avgLast10.mygoal, min: minRatings.mygoal, max: maxRatings.mygoal, fullMark: 10 },
    { subject: 'Intensity', value: ratings.intensity, average: avgLast10.intensity, min: minRatings.intensity, max: maxRatings.intensity, fullMark: 10 }
  ];

  return (
    <div className={`min-h-screen ${theme.bg} flex flex-col transition-colors duration-300`} data-testid="page-last-training">
      <header className="flex items-center justify-between p-4 pt-6">
        <button data-testid="back-button" onClick={() => navigate("/")} className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${theme.button}`}>
          <ArrowLeft className={`w-5 h-5 ${theme.textPrimary}`} />
        </button>
        <h1 className={`text-xl font-bold ${theme.textPrimary}`}>{isCoach ? 'Team Training' : 'Rate Training'}</h1>
        <button data-testid="history-button" onClick={() => navigate(isCoach ? "/team/sessions" : "/sessions")} className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${theme.button}`}>
          <History className={`w-5 h-5 ${isDark ? 'text-white/70' : 'text-[#493986]/70'}`} />
        </button>
      </header>

      <div className="flex-1 px-4 pb-8 overflow-y-auto">
        {loading ? (
          <div className={`text-center py-8 ${theme.textMuted}`}>Loading...</div>
        ) : !session ? (
          <div className={`text-center py-12 ${theme.textMuted}`}>
            <Star className={`w-16 h-16 mx-auto mb-4 ${theme.textMuted}`} />
            <p className="text-lg font-medium mb-2">No training sessions yet</p>
            <p className="text-sm mb-6">{isCoach ? 'Your team has no sessions yet' : 'Add a training session first to rate it'}</p>
            <button onClick={() => navigate(isCoach ? "/team/sessions" : "/sessions")} className="px-6 py-3 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl">
              {isCoach ? 'View Team Sessions' : 'Add Training Session'}
            </button>
          </div>
        ) : (
          <>
            <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#493986]/20 to-[#92a7e8]/20 flex items-center justify-center">
                  <Activity className={`w-6 h-6 ${theme.textPrimary}`} />
                </div>
                <div className="flex-1">
                  <h3 className={`font-semibold ${theme.textPrimary}`}>{session.name}</h3>
                  <p className={`text-sm ${theme.textMuted}`}>{session.type} • {new Date(session.completed_at).toLocaleDateString()} • {session.duration} min</p>
                </div>
                {/* Show rated badge when session is rated */}
                {!isCoach && (session.rating || justRated) && (
                  <span className={`text-xs px-3 py-1.5 rounded-full font-medium ${isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'}`}>
                    <CheckCircle2 className="w-3 h-3 inline mr-1" />
                    Rated
                  </span>
                )}
              </div>
              {isCoach && teamStats && (
                <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-[#493986]/10'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-sm font-medium ${theme.textPrimary}`}>Player Completion</span>
                    <span className={`text-sm font-bold ${teamStats.completion_rate >= 80 ? 'text-green-500' : teamStats.completion_rate >= 50 ? 'text-yellow-500' : 'text-red-500'}`}>
                      {teamStats.athletes_rated}/{teamStats.sessions_today || teamStats.total_athletes} rated
                    </span>
                  </div>
                  <div className={`h-3 rounded-full ${isDark ? 'bg-white/10' : 'bg-[#493986]/10'} overflow-hidden`}>
                    <div className={`h-full rounded-full transition-all duration-500 ${teamStats.completion_rate >= 80 ? 'bg-green-500' : teamStats.completion_rate >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${teamStats.completion_rate}%` }} />
                  </div>
                  <p className={`text-xs mt-2 ${theme.textMuted}`}>{teamStats.completion_rate}% of players have rated this training</p>
                </div>
              )}
            </div>

            {/* Show radar chart for COACH only (athletes have it below sliders) */}
            {isCoach && (
              <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className={`font-semibold ${theme.textPrimary}`}>Team Average vs Last 10</h3>
                  <div className="flex items-center gap-2 text-xs flex-wrap">
                    <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-[#493986]" /><span className={theme.textMuted}>Avg</span></span>
                    <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-red-400" /><span className={theme.textMuted}>Min</span></span>
                    <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-green-400" /><span className={theme.textMuted}>Max</span></span>
                    <span className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-yellow-500" /><span className={theme.textMuted}>Hist</span></span>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke={isDark ? 'rgba(255,255,255,0.1)' : 'rgba(73,57,134,0.1)'} />
                    <PolarAngleAxis dataKey="subject" tick={{ fill: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(73,57,134,0.6)', fontSize: 12 }} />
                    <PolarRadiusAxis domain={[0, 10]} tick={{ fill: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(73,57,134,0.4)', fontSize: 10 }} />
                    {/* Coach: Show min boundary (red dashed) */}
                    {minRatings.fun > 0 && <Radar name="Min" dataKey="min" stroke="#f87171" fill="#f87171" fillOpacity={0.1} strokeDasharray="4 4" />}
                    {/* Coach: Show max boundary (green dashed) */}
                    {maxRatings.fun > 0 && <Radar name="Max" dataKey="max" stroke="#4ade80" fill="#4ade80" fillOpacity={0.1} strokeDasharray="4 4" />}
                    {/* Historical average (yellow) */}
                    {avgLast10.fun > 0 && <Radar name="Last 10 Avg" dataKey="average" stroke="#eab308" fill="#eab308" fillOpacity={0.15} />}
                    {/* Current average (purple solid) */}
                    <Radar name="Rating" dataKey="value" stroke="#493986" fill="#493986" fillOpacity={0.5} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* ATHLETE: Show editable sliders when NOT rated yet - SLIDERS FIRST */}
            {!isCoach && !session.rating && !justRated && (
              <>
                <div className="space-y-3 mb-4">
                  {ratingTopics.map(topic => (
                    <RatingSlider key={topic.key} value={ratings[topic.key]} onChange={(val) => setRatings({...ratings, [topic.key]: val})} label={topic.question.en} infoText={topic.tooltip.en} infoKey={topic.key} icon={topic.icon} color={topic.color} isDark={isDark} disabled={saving} />
                  ))}
                </div>

                <RatingMessageField key={session.id} ref={messageRef} theme={theme} disabled={saving} sessionId={session.id} />

                {/* Radar chart preview BELOW sliders for athlete */}
                <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
                  <h3 className={`font-semibold mb-2 ${theme.textPrimary}`}>Rating Preview</h3>
                  <ResponsiveContainer width="100%" height={180}>
                    <RadarChart data={radarData}>
                      <PolarGrid stroke={isDark ? 'rgba(255,255,255,0.1)' : 'rgba(73,57,134,0.1)'} />
                      <PolarAngleAxis dataKey="subject" tick={{ fill: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(73,57,134,0.6)', fontSize: 12 }} />
                      <PolarRadiusAxis domain={[0, 10]} tick={{ fill: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(73,57,134,0.4)', fontSize: 10 }} />
                      <Radar name="Rating" dataKey="value" stroke="#493986" fill="#493986" fillOpacity={0.5} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>

                <button data-testid="save-rating-button" onClick={handleSaveRating} disabled={saving} className="w-full py-4 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all disabled:opacity-50">
                  {saving ? "Saving..." : "Save Rating"}
                </button>
              </>
            )}

            {!isCoach && (session.rating || justRated) && <RatingMessageField key={session.id} theme={theme} sessionId={session.id} readOnly />}

            {/* ATHLETE: Show read-only horizontal bar charts when RATED */}
            {!isCoach && (session.rating || justRated) && (
              <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`} data-testid="rating-summary-card">
                <h3 className={`font-semibold mb-4 ${theme.textPrimary}`}>Your Rating</h3>
                <div className="space-y-3">
                  {ratingTopics.map(topic => (
                    <RatingBarChart 
                      key={topic.key}
                      label={topic.label}
                      value={ratings[topic.key]}
                      icon={topic.icon}
                      color={topic.color}
                      bgColor={topic.bgColor}
                      isDark={isDark}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Streak Bonus Animation - shown when all trainings are rated */}
            {!isCoach && showStreakBonus && (
              <div className={`p-6 rounded-2xl ${isDark ? 'bg-gradient-to-br from-yellow-500/20 via-orange-500/20 to-red-500/20' : 'bg-gradient-to-br from-yellow-100 via-orange-100 to-red-100'} border ${isDark ? 'border-yellow-500/30' : 'border-yellow-300'} mb-4 text-center animate-pulse`} data-testid="streak-bonus-card">
                <div className="flex justify-center gap-2 mb-3">
                  <Flame className="w-8 h-8 text-orange-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <Trophy className="w-8 h-8 text-yellow-500 animate-bounce" style={{ animationDelay: '100ms' }} />
                  <Flame className="w-8 h-8 text-orange-500 animate-bounce" style={{ animationDelay: '200ms' }} />
                </div>
                <h3 className={`text-xl font-bold mb-1 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>Streak Bonus!</h3>
                <p className={`text-sm ${isDark ? 'text-yellow-300/80' : 'text-yellow-700'}`}>Amazing! You've rated all your trainings</p>
                <p className={`text-xs mt-2 ${theme.textMuted}`}>Keep up the great work!</p>
              </div>
            )}

            {/* Last Training Reviews Card - shown after rating */}
            {!isCoach && (session.rating || justRated) && (
              <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl`} data-testid="last-training-reviews-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500/20 to-green-600/20 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${theme.textPrimary}`}>Training Reviews</h3>
                    <p className={`text-xs ${theme.textMuted}`}>Your rating has been saved</p>
                  </div>
                </div>

                {/* Older trainings to review */}
                {unratedSessions.length > 0 ? (
                  <div>
                    <p className={`text-sm font-medium mb-3 ${theme.textSecondary}`}>
                      <Clock className="w-4 h-4 inline mr-1" />
                      Older trainings to review
                    </p>
                    <div className="space-y-2">
                      {unratedSessions.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => handleRateOlderSession(s)}
                          className={`w-full p-3 rounded-xl ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-[#493986]/5 hover:bg-[#493986]/10'} flex items-center justify-between transition-all`}
                          data-testid={`rate-session-${s.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <Activity className={`w-5 h-5 ${theme.textMuted}`} />
                            <div className="text-left">
                              <p className={`text-sm font-medium ${theme.textPrimary}`}>{s.name}</p>
                              <p className={`text-xs ${theme.textMuted}`}>{s.type} • {new Date(s.completed_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <ChevronRight className={`w-5 h-5 ${theme.textMuted}`} />
                        </button>
                      ))}
                    </div>
                  </div>
                ) : !showStreakBonus ? (
                  <div className={`text-center py-4 rounded-xl ${isDark ? 'bg-green-500/10' : 'bg-green-50'}`}>
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
                    <p className={`font-medium text-green-500`}>All good!</p>
                    <p className={`text-xs ${theme.textMuted} mt-1`}>You've rated all your trainings</p>
                  </div>
                ) : null}

                {/* Back to dashboard button */}
                <button
                  onClick={() => navigate("/")}
                  className={`w-full mt-4 py-3 rounded-xl ${isDark ? 'bg-white/10 hover:bg-white/15' : 'bg-[#493986]/10 hover:bg-[#493986]/15'} ${theme.textPrimary} font-medium transition-all`}
                >
                  Back to Dashboard
                </button>
              </div>
            )}

            {/* COACH: Show bandwidth bars with min-avg-max */}
            {isCoach && (
              <>
                <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`font-semibold ${theme.textPrimary}`}>Team Rating Bandwidth</h3>
                    {teamStats && (
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold ${isDark ? 'bg-white/10 text-white/80' : 'bg-[#493986]/10 text-[#493986]'}`}
                        data-testid="team-bandwidth-filled-in"
                      >
                        {teamStats.athletes_rated ?? 0}/{teamStats.total_athletes ?? 0} filled in
                      </span>
                    )}
                  </div>
                  <div className="space-y-4">
                    {ratingTopics.map(topic => {
                      const avg = ratings[topic.key] || 0;
                      const min = minRatings[topic.key] || 0;
                      const max = maxRatings[topic.key] || 0;
                      const Icon = topic.icon;
                      return (
                        <div key={topic.key} className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Icon className={`w-4 h-4 ${topic.color}`} />
                              <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>{topic.label}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                              <span className={`px-2 py-0.5 rounded ${isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'}`}>{min}</span>
                              <span className={`px-2 py-0.5 rounded font-bold ${isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600'}`}>{avg}</span>
                              <span className={`px-2 py-0.5 rounded ${isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'}`}>{max}</span>
                            </div>
                          </div>
                          {/* Bandwidth bar showing range */}
                          <div className={`relative h-4 rounded-full ${isDark ? 'bg-white/10' : 'bg-gray-200'} overflow-hidden`}>
                            {/* Range band (min to max) */}
                            <div 
                              className={`absolute h-full ${isDark ? 'bg-gradient-to-r from-red-500/40 via-purple-500/40 to-green-500/40' : 'bg-gradient-to-r from-red-200 via-purple-200 to-green-200'}`}
                              style={{ 
                                left: `${(min / 10) * 100}%`, 
                                width: `${((max - min) / 10) * 100}%` 
                              }}
                            />
                            {/* Min marker */}
                            <div 
                              className="absolute top-0 bottom-0 w-1 bg-red-500 rounded"
                              style={{ left: `${(min / 10) * 100}%` }}
                            />
                            {/* Average marker */}
                            <div 
                              className="absolute top-0 bottom-0 w-1.5 bg-[#493986] rounded z-10"
                              style={{ left: `${(avg / 10) * 100}%` }}
                            />
                            {/* Max marker */}
                            <div 
                              className="absolute top-0 bottom-0 w-1 bg-green-500 rounded"
                              style={{ left: `${(max / 10) * 100}%` }}
                            />
                          </div>
                          {/* Historical average indicator */}
                          {avgLast10[topic.key] > 0 && (
                            <div className="flex items-center justify-between mt-1">
                              <span className={`text-xs ${theme.textMuted}`}>Historical avg: {avgLast10[topic.key]}</span>
                              <span className={`text-xs ${avg > avgLast10[topic.key] ? 'text-green-500' : avg < avgLast10[topic.key] ? 'text-red-500' : theme.textMuted}`}>
                                {avg > avgLast10[topic.key] ? '↑' : avg < avgLast10[topic.key] ? '↓' : '='} {Math.abs(avg - avgLast10[topic.key]).toFixed(1)} vs history
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {/* Legend */}
                  <div className={`mt-4 pt-3 border-t ${isDark ? 'border-white/10' : 'border-gray-200'} flex items-center justify-center gap-4 text-xs`}>
                    <span className="flex items-center gap-1"><div className="w-2 h-2 rounded bg-red-500" /> Min</span>
                    <span className="flex items-center gap-1"><div className="w-2 h-2 rounded bg-[#493986]" /> Avg</span>
                    <span className="flex items-center gap-1"><div className="w-2 h-2 rounded bg-green-500" /> Max</span>
                  </div>
                </div>
                <button onClick={() => navigate("/team/sessions")} className="w-full py-4 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all">
                  View All Team Sessions
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LastTrainingPage;
